import plur from 'plur';
import { z } from '@customerio/hydra-validation';
import type { CustomersDeleteParams } from '@customerio/hydra-ui-api';
import { selectionExceedsLoaded, type PeopleSelectionSummary } from '@/features/people';
import type { Customer } from '@/services/db/customer/entity';
import { cioId, hasConfirmedDoubleOptIn } from '@/services/db/customer/utils';
import { formatNumber } from '@/utils/number';
import { isRecord } from '@/utils/objects';
import { normalizedPeopleFilterEnvelope } from '../components/people-filter-builder/filters-param';

/**
 * Gating and payload logic for the People-list bulk actions. Each predicate is
 * one selection-state branch: most actions operate per-loaded-row, so they
 * disable when the selection covers more profiles than the page holds
 * (`selectionExceedsLoaded`); delete instead switches to its filter-based
 * payload in that state.
 */

/** Unsubscribe acts on loaded rows only. */
export function canUnsubscribe(selection: PeopleSelectionSummary): boolean {
  return !selectionExceedsLoaded(selection) && selection.selectedCount > 0;
}

/** Add-to-manual-segment is a Builder-plan action and adds by profile id. */
export function canAddToManualSegment(
  selection: PeopleSelectionSummary,
  { isBuilder }: { isBuilder: boolean },
): boolean {
  if (!isBuilder) return false;
  if (selectionExceedsLoaded(selection)) return false;
  return selection.selectedCount > 0;
}

/** Inviting test users is a Builder-plan action requiring edit permission. */
export function canInviteTestUsers(
  selection: PeopleSelectionSummary,
  { isBuilder, canEdit }: { isBuilder: boolean; canEdit: boolean },
): boolean {
  if (!isBuilder || !canEdit) return false;
  if (selectionExceedsLoaded(selection)) return false;
  return selection.selectedCount > 0;
}

/**
 * Setting per-contact email tracking consent updates each loaded record, so
 * the filter-based select-all path (no loaded records to update) is out.
 */
export function canSetEmailTrackingConsent(
  selection: PeopleSelectionSummary,
  { consentFlagEnabled, canEdit }: { consentFlagEnabled: boolean; canEdit: boolean },
): boolean {
  if (!consentFlagEnabled || !canEdit) return false;
  if (selectionExceedsLoaded(selection)) return false;
  return selection.selectedCount > 0;
}

/** The select-all-across-pages state, where delete goes by filter instead of ids. */
export function isBulkDeletion(selection: PeopleSelectionSummary): boolean {
  return selectionExceedsLoaded(selection);
}

/**
 * Delete allows the filter-based select-all path — but only when a search is
 * active (otherwise one page holds everything and explicit ids are used).
 * The filter's shape is `buildBulkDeleteMatch`'s concern: legacy bare-array
 * deep links resolve now that the people-filter normalizer converts them,
 * and a param that resolves to nothing surfaces as the confirm-time error.
 */
export function canDelete(
  selection: PeopleSelectionSummary,
  { noFiltersApplied }: { noFiltersApplied: boolean },
): boolean {
  if (isBulkDeletion(selection)) {
    return !noFiltersApplied;
  }
  return selection.selectedCount > 0;
}

/** Merge takes exactly 2 profiles and edit permission. */
export function isMergeDisabled(
  { allSelected, totalCount, selectedCount }: PeopleSelectionSummary,
  canEdit: boolean,
): boolean {
  return !canEdit || selectedCount > 2 || (allSelected && totalCount > 2) || totalCount < 2;
}

export function inviteTestUsersLabel(selectedCount: number): string {
  return selectedCount === 1 ? 'Invite test user' : 'Invite test users';
}

/** "N selected people" — the count is the whole result set when all are selected. */
export function selectedPeopleLabel(
  { allSelected, totalCount, selectedCount }: PeopleSelectionSummary,
  { personSingular, personPlural }: { personSingular: string; personPlural: string },
): string {
  const count = allSelected ? totalCount : selectedCount;
  return `${formatNumber(count)} selected ${plur(personSingular, personPlural, count)}`;
}

/* ------------------------------------------------------------------------- */
/* Delete payload                                                             */
/* ------------------------------------------------------------------------- */

type DeleteFilterWire = NonNullable<CustomersDeleteParams['filters']>;

/** The match half of the bulk-delete body (`suppress` is merged in by the caller). */
export type BulkDeleteMatch = Pick<CustomersDeleteParams, 'type' | 'internal_ids' | 'email' | 'filters'>;

/**
 * The structured filter wire (`{ and: [...] }` / `{ or: [...] }` / a single
 * leaf) is the shape the delete endpoint takes. The param routes through the
 * shared people-filter normalizer first, so the legacy bare-array wire from
 * old deep links converts to the same proto tree the list query and the
 * export send — all three boundaries target the same audience. When the
 * normalizer can't produce a same-audience tree (an unrecognized wire, or a
 * legacy row it can't model — which converts to a constrain-nobody null
 * leaf), the envelope is null and the delete refuses via the no-valid-match
 * path below instead of posting bytes with a different meaning.
 */
const StructuredFilterWire = z.custom<DeleteFilterWire>(isRecord);

/**
 * The delete request's match strategy: explicit ids for a per-row selection
 * (or a select-all that fits on one page), the condition filter or email
 * search for a cross-page select-all. `null` when no valid match exists —
 * callers never send a delete in that state. A URL carrying both search
 * modes at once is also refused: the list queries their intersection, but
 * this payload can send only one, which would delete more than the list
 * showed.
 */
export function buildBulkDeleteMatch({
  selection,
  noFiltersApplied,
  selectedCioIds,
  filters,
  email,
}: {
  selection: PeopleSelectionSummary;
  noFiltersApplied: boolean;
  selectedCioIds: readonly string[];
  filters: string | null;
  email: string;
}): BulkDeleteMatch | null {
  if (!isBulkDeletion(selection) || noFiltersApplied) {
    return { type: 'internal_ids', internal_ids: [...selectedCioIds] };
  }
  // Both search modes at once needs a stale or hand-built URL (the UI clears
  // one param when activating the other), but the state is reachable — and
  // the visible audience is the intersection, which no single-mode payload
  // can express.
  if (filters != null && filters !== '' && email !== '') {
    return null;
  }
  if (filters != null && filters !== '') {
    const decoded = StructuredFilterWire.safeParse(normalizedPeopleFilterEnvelope(filters));
    return decoded.success ? { type: 'filters', filters: decoded.data } : null;
  }
  if (email !== '') {
    return { type: 'email', email };
  }
  return null;
}

/* ------------------------------------------------------------------------- */
/* Invite test users                                                          */
/* ------------------------------------------------------------------------- */

export interface InviteTestUsersPartition {
  /** Profiles eligible for the double-opt-in invite, by `cio_id`. */
  cioIds: string[];
  alreadyVerified: number;
  skippedNoEmail: number;
  /** Profiles that can't be invited at all (no `cio_id`) — counted as failures. */
  preFailed: number;
}

/** Split the selection into invitable profiles and the skip/failure tallies. */
export function partitionInviteTestUsers(
  people: ReadonlyArray<Pick<Customer, 'attributes' | 'identifiers'>>,
): InviteTestUsersPartition {
  const partition: InviteTestUsersPartition = { cioIds: [], alreadyVerified: 0, skippedNoEmail: 0, preFailed: 0 };
  for (const person of people) {
    const email = person.attributes.email;
    if (email == null || email === '') {
      partition.skippedNoEmail += 1;
      continue;
    }
    if (hasConfirmedDoubleOptIn(person.attributes)) {
      partition.alreadyVerified += 1;
      continue;
    }
    const id = cioId(person);
    if (id == null) {
      partition.preFailed += 1;
      continue;
    }
    partition.cioIds.push(id);
  }
  return partition;
}

/**
 * The invite outcome snackbar: each non-zero tally becomes a sentence, joined
 * with periods; an empty selection outcome gets the standalone message.
 */
export function inviteOutcomeMessage({
  invited,
  alreadyVerified,
  skippedNoEmail,
  failed,
  personSingular,
  personPlural,
}: {
  invited: number;
  alreadyVerified: number;
  skippedNoEmail: number;
  failed: number;
  personSingular: string;
  personPlural: string;
}): string {
  const parts: string[] = [];
  if (invited > 0) {
    const noun = invited === 1 ? personSingular : personPlural;
    parts.push(`Sent confirmation to add ${formatNumber(invited)} ${noun} as test ${plur('user', invited)}`);
  }
  if (alreadyVerified > 0) parts.push(`${formatNumber(alreadyVerified)} already verified`);
  if (skippedNoEmail > 0) parts.push(`${formatNumber(skippedNoEmail)} skipped (no email)`);
  if (failed > 0) parts.push(`${formatNumber(failed)} failed`);
  if (parts.length === 0) {
    return `No ${personPlural} to invite as test users`;
  }
  return `${parts.join('. ')}.`;
}

/* ------------------------------------------------------------------------- */
/* Merge                                                                      */
/* ------------------------------------------------------------------------- */

export interface MergeLinkParams {
  source: 'people_list';
  email?: string;
  filters?: string;
  primary_id?: string;
  secondary_id?: string;
}

/**
 * Query params for the merge page: the current search carries over, and with
 * two profiles selected the older one (by `created_at`) becomes the primary.
 * The snake_case pair is the URL spelling the embedded merge route reads —
 * its controller aliases them onto its own camelCase properties.
 */
export function buildMergeLinkParams({
  selectedPeople,
  email,
  filters,
}: {
  selectedPeople: ReadonlyArray<Pick<Customer, 'attributes' | 'identifiers'>>;
  email: string;
  filters: string | null;
}): MergeLinkParams {
  const params: MergeLinkParams = { source: 'people_list' };
  if (email !== '') params.email = email;
  if (filters != null && filters !== '') params.filters = filters;

  if (selectedPeople.length === 1) {
    params.primary_id = cioId(selectedPeople[0]);
  } else if (selectedPeople.length === 2) {
    const oldestFirst = [...selectedPeople].sort(
      (a, b) => Number(a.attributes.created_at) - Number(b.attributes.created_at),
    );
    params.primary_id = cioId(oldestFirst[0]);
    params.secondary_id = cioId(oldestFirst[1]);
  }
  return params;
}
